import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment-timezone';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';

import { OrderFeedbackEntry, OrderFeedbackForOrder } from '../../../models/review';
import { FNFResponse } from '../../../models/response';
import { OrderService } from '../../../services/order.service';
import { RestaurantService } from '../../../services/restaurant.service';
import { ReviewService } from '../../../services/review.service';

/**
 * Guest feedback on one order — Figma 379:8108.
 *
 * Read-only, and the page says so. Order feedback is private to the
 * restaurant and never touches the public rating, so unlike a restaurant
 * review there is nothing here to reply to, report, or remove.
 */
@Component({
  selector: 'app-order-feedback',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './order-feedback.component.html',
  styleUrl: './order-feedback.component.css',
})
export class OrderFeedbackComponent {
  readonly orderId = inject(ActivatedRoute).snapshot.params['order_id'] as string;

  private reviewService = inject(ReviewService);
  private orderService = inject(OrderService);
  private restaurantService = inject(RestaurantService);
  private toastr = inject(ToastrService);
  private spinner = inject(NgxSpinnerService);
  private router = inject(Router);

  feedback: OrderFeedbackForOrder | null = null;
  order: any;
  restaurantTimezone: any;
  loadFailed = false;

  constructor() {
    this.restaurantService.currentRestaurantDetails$.subscribe((restaurant: any) => {
      this.restaurantTimezone = restaurant?.time_zone;
    });
  }

  ngOnInit() {
    this.spinner.show();

    // The order itself, for the summary card the design leads with.
    this.orderService.getOrderById(this.orderId).subscribe({
      next: (response: FNFResponse<any>) => {
        if (response?.data) this.order = response.data;
      },
    });

    this.reviewService.getFeedbackForOrder(this.orderId).subscribe({
      next: (response: FNFResponse<OrderFeedbackForOrder>) => {
        this.spinner.hide();
        if (!response?.data) {
          this.loadFailed = true;
          const message =
            response?.error?.error?.message ||
            response?.error?.message ||
            'Could not load feedback';
          this.toastr.error(message, 'Error');
          return;
        }
        this.feedback = response.data;
      },
      error: () => {
        this.spinner.hide();
        this.loadFailed = true;
        this.toastr.error('Could not load feedback', 'Error');
      },
    });
  }

  navigateBack() {
    this.router.navigate(['/main/order']);
  }

  stars(rating: number): string {
    const filled = Math.max(0, Math.min(5, Math.round(rating)));
    return '★'.repeat(filled) + '☆'.repeat(5 - filled);
  }

  /** "AR" for the avatar chip. Empty when the order never named the guest. */
  initials(entry: OrderFeedbackEntry): string {
    const parts = (entry.guest_name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  formatDateTime(value?: string | null): string {
    if (!value) return '';
    const timestamp = this.restaurantTimezone
      ? moment.tz(value, this.restaurantTimezone)
      : moment(value);
    return timestamp.format('MMM D, YYYY · h:mm A');
  }

  formatOrderPlaced(): string {
    if (!this.order?.created_at) return '';
    const timestamp = this.restaurantTimezone
      ? moment.tz(this.order.created_at, this.restaurantTimezone)
      : moment(this.order.created_at);
    return timestamp.format('MMM D · h:mm A');
  }

  get tableLabel(): string {
    const type = this.order?.order_type;
    if (type === 'togo' || type === 'togo_dine_in') return 'ToGo';
    if (type === 'online') return this.order?.online_platform_type || 'Online';
    return this.order?.tables?.[0]?.table_name || 'N/A';
  }

  get customerName(): string {
    const customer = this.order?.customers?.[0];
    if (!customer) return 'Guest';
    return [customer.first_name, customer.last_name].filter(Boolean).join(' ') || 'Guest';
  }

  get orderReference(): string {
    return this.order?.order_number ? `ORD-${this.order.order_number}` : this.orderId;
  }

  get entries(): OrderFeedbackEntry[] {
    return this.feedback?.items || [];
  }

  get hasEntries(): boolean {
    return this.entries.length > 0;
  }

  /** Null — never 0 — when nobody rated the order. */
  get averageRating(): number | null {
    return this.feedback?.average_rating ?? null;
  }

  get entryCount(): number {
    return this.feedback?.entry_count || 0;
  }
}
